import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, testId, textarea, ...props }) => (
  <div>
    <label className="elt-label">{label}</label>
    {textarea ? <textarea data-testid={testId} className="elt-input resize-none" rows={3} {...props} /> : <input data-testid={testId} className="elt-input" {...props} />}
  </div>
);

const Toggle = ({ label, value, onChange, testId }) => (
  <button type="button" data-testid={testId} onClick={() => onChange(!value)}
    className={`flex items-center gap-3 text-[0.68rem] tracking-[0.2em] uppercase px-4 py-3 border transition-colors duration-300 ${value ? "border-[var(--elt-gold)] text-[var(--elt-gold-light)]" : "border-white/12 text-[var(--elt-muted)] hover:border-white/30"}`}>
    <span className={`w-3.5 h-3.5 border flex items-center justify-center ${value ? "border-[var(--elt-gold)] bg-[var(--elt-gold)]" : "border-white/30"}`}>
      {value && <Check size={10} className="text-[#0B0C0E]" />}
    </span>
    {label}
  </button>
);

export default function RequestJourney() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);
  const [form, setForm] = useState({
    pickup: "", destination: "", date: "", time: "", passengers: 2,
    experience: "", vehicle: params.get("vehicle") || "",
    child_seats: false, meet_greet: false, multiple_stops: false,
    luggage: "", language_preference: "", notes: "",
    name: "", email: "", phone: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setV = (k, v) => setForm({ ...form, [k]: v });

  const steps = [t("form.step1"), t("form.step2"), t("form.step3"), t("form.step4")];
  const experiences = t("form.experiences");

  const next = () => {
    if (step === 0 && (!form.pickup || !form.destination || !form.date || !form.time)) return toast.error(t("form.required"));
    if (step === 1 && !form.experience) return toast.error(t("form.required"));
    setStep(step + 1);
  };

  const submit = async () => {
    if (!form.name || !form.email) return toast.error(t("form.required"));
    setSending(true);
    try {
      const res = await axios.post(`${API}/requests/journey`, { ...form, passengers: Number(form.passengers) || 1 });
      setReference(res.data.reference);
    } catch { toast.error("Something went wrong. Please try again."); }
    setSending(false);
  };

  return (
    <main className="min-h-screen">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 pt-36 pb-28">
        <Link to="/" data-testid="request-back-home-link" className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.25em] uppercase text-[var(--elt-muted)] hover:text-[var(--elt-gold-light)] transition-colors mb-12">
          <ArrowLeft size={13} /> {t("nav.home")}
        </Link>

        {reference ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center py-16" data-testid="request-success">
            <span className="gold-rule mb-8" />
            <h1 className="font-serif-elt text-3xl md:text-4xl font-light text-[var(--elt-gold-light)] leading-snug">{t("form.successTitle")}</h1>
            <p className="mt-8 text-sm font-light text-[var(--elt-muted)] max-w-md mx-auto leading-relaxed">{t("form.successBody")}</p>
            <p className="mt-8 text-[0.7rem] tracking-[0.3em] uppercase text-[var(--elt-gold)]" data-testid="request-reference">{t("form.reference")} — {reference}</p>
            <Link to="/" className="btn-ghost mt-12">{t("nav.home")}</Link>
          </motion.div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-4">
              <span className="gold-rule" />
              <span className="eyebrow">{t("cta.request")}</span>
            </div>
            <div className="flex gap-1 mb-4">
              {steps.map((s, i) => (
                <div key={s} className={`h-px flex-1 transition-colors duration-500 ${i <= step ? "bg-[var(--elt-gold)]" : "bg-white/10"}`} />
              ))}
            </div>
            <p className="text-[0.62rem] tracking-[0.3em] uppercase text-[var(--elt-muted)] mb-3">{`0${step + 1} / 04`}</p>
            <h1 className="font-serif-elt text-3xl md:text-4xl font-light text-[var(--elt-ivory)] mb-12" data-testid="request-step-title">{steps[step]}</h1>

            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                {step === 0 && (
                  <div className="space-y-8">
                    <div className="grid sm:grid-cols-2 gap-8">
                      <Field label={t("form.pickup")} testId="journey-pickup-input" value={form.pickup} onChange={set("pickup")} placeholder="Paris, Hôtel Ritz…" />
                      <Field label={t("form.destination")} testId="journey-destination-input" value={form.destination} onChange={set("destination")} placeholder="Monaco…" />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-8">
                      <Field label={t("form.date")} testId="journey-date-input" type="date" value={form.date} onChange={set("date")} />
                      <Field label={t("form.time")} testId="journey-time-input" type="time" value={form.time} onChange={set("time")} />
                      <Field label={t("form.passengers")} testId="journey-passengers-input" type="number" min={1} max={50} value={form.passengers} onChange={set("passengers")} />
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {experiences.map((exp, i) => (
                      <button key={exp} type="button" data-testid={`experience-option-${i}`} onClick={() => setV("experience", exp)}
                        className={`text-left px-6 py-6 border transition-colors duration-400 ${form.experience === exp ? "border-[var(--elt-gold)] bg-[var(--elt-gold)]/5" : "border-white/10 hover:border-white/30"}`}>
                        <span className={`font-serif-elt text-xl font-light ${form.experience === exp ? "text-[var(--elt-gold-light)]" : "text-[var(--elt-ivory)]"}`}>{exp}</span>
                      </button>
                    ))}
                    {form.vehicle && <p className="sm:col-span-2 text-xs font-light text-[var(--elt-gold)] tracking-wide">Vehicle: {form.vehicle}</p>}
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-8">
                    <div className="flex flex-wrap gap-3">
                      <Toggle label={t("form.childSeats")} value={form.child_seats} onChange={(v) => setV("child_seats", v)} testId="toggle-child-seats" />
                      <Toggle label={t("form.meetGreet")} value={form.meet_greet} onChange={(v) => setV("meet_greet", v)} testId="toggle-meet-greet" />
                      <Toggle label={t("form.multipleStops")} value={form.multiple_stops} onChange={(v) => setV("multiple_stops", v)} testId="toggle-multiple-stops" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-8">
                      <Field label={t("form.luggage")} testId="journey-luggage-input" value={form.luggage} onChange={set("luggage")} placeholder="3 suitcases, 2 carry-on…" />
                      <Field label={t("form.languagePref")} testId="journey-language-input" value={form.language_preference} onChange={set("language_preference")} placeholder="English, Français…" />
                    </div>
                    <Field label={t("form.notes")} testId="journey-notes-input" textarea value={form.notes} onChange={set("notes")} />
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-8">
                    <Field label={t("form.name")} testId="journey-name-input" value={form.name} onChange={set("name")} />
                    <div className="grid sm:grid-cols-2 gap-8">
                      <Field label={t("form.email")} testId="journey-email-input" type="email" value={form.email} onChange={set("email")} />
                      <Field label={t("form.phone")} testId="journey-phone-input" value={form.phone} onChange={set("phone")} />
                    </div>
                    <div className="border border-white/8 p-6 text-xs font-light text-[var(--elt-muted)] leading-relaxed">
                      {form.pickup} → {form.destination} · {form.date} {form.time} · {form.passengers} pax · {form.experience}{form.vehicle ? ` · ${form.vehicle}` : ""}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-14 flex items-center justify-between">
              <button type="button" data-testid="journey-back-btn" onClick={() => setStep(Math.max(0, step - 1))}
                className={`text-[0.68rem] tracking-[0.25em] uppercase text-[var(--elt-muted)] hover:text-[var(--elt-ivory)] transition-colors ${step === 0 ? "invisible" : ""}`}>
                ← {t("form.back")}
              </button>
              {step < 3 ? (
                <button type="button" data-testid="journey-next-btn" onClick={next} className="btn-gold">{t("form.next")}</button>
              ) : (
                <button type="button" data-testid="journey-submit-btn" onClick={submit} disabled={sending} className="btn-gold disabled:opacity-50">{t("form.submit")}</button>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </main>
  );
}
